import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Grid,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const ProductCapacitiesTab = ({ locationId, isNewLocation = false, formCapacities = [], setFormCapacities }) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const [capacities, setCapacities] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    variant_id: '',
    capacity: '',
    setupTime: '',
    cleanupTime: ''
  });

  // Product selection dialog states
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProductsForDialog, setSelectedProductsForDialog] = useState([]);
  const [selectedVariantsForDialog, setSelectedVariantsForDialog] = useState({});

  // Load products and capacities on component mount
  useEffect(() => {
    loadProducts();
    if (locationId && !isNewLocation) {
      loadCapacities();
    }
  }, [companyId, locationId, isNewLocation]);

  const loadCapacities = async () => {
    if (!locationId) return;
    
    try {
      const { data, error } = await supabase
        .from('location_product_capacities')
        .select(`
          *,
          product:product_id(
            id,
            name,
            sku,
            brand:brand_id(name),
            variants:product_variants(
              id,
              name,
              sku,
              volume_weight,
              volume_unit,
              pack_quantity
            )
          )
        `)
        .eq('company_id', companyId)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCapacities(data || []);
    } catch (err) {
      console.error('Error loading capacities:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          sku,
          brand:brand_id(name),
          variants:product_variants(
            id,
            name,
            sku,
            volume_weight,
            volume_unit,
            pack_quantity
          )
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const handleOpenDialog = (capacity = null) => {
    if (capacity) {
      setEditMode(true);
      setFormData(capacity);
    } else {
      setEditMode(false);
      setFormData({
        product_id: '',
        variant_id: '',
        capacity: '',
        setupTime: '',
        cleanupTime: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData({
      product_id: '',
      variant_id: '',
      capacity: '',
      setupTime: '',
      cleanupTime: ''
    });
  };

  // Product selection dialog handlers
  const handleOpenProductSelectionDialog = () => {
    setProductSelectionDialogOpen(true);
    setProductSearchTerm('');
    setSelectedProductsForDialog([]);
    setSelectedVariantsForDialog({});
  };

  const handleCloseProductSelectionDialog = () => {
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setSelectedProductsForDialog([]);
    setSelectedVariantsForDialog({});
  };

  // Handle variant selection
  const handleVariantSelection = (productId, variantId, isSelected) => {
    setSelectedVariantsForDialog(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [variantId]: isSelected
      }
    }));
  };

  // Select all variants for a product
  const handleSelectAllVariants = (productId, variants) => {
    const allSelected = variants.every(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    );
    
    const newSelection = {};
    variants.forEach(variant => {
      newSelection[variant.id] = !allSelected;
    });
    
    setSelectedVariantsForDialog(prev => ({
      ...prev,
      [productId]: newSelection
    }));
  };

  // Check if all variants are selected for a product
  const areAllVariantsSelected = (productId, variants) => {
    return variants.every(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    );
  };

  // Get selected variants count for a product
  const getSelectedVariantsCount = (productId, variants) => {
    return variants.filter(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    ).length;
  };

  const handleConfirmProductSelection = () => {
    // Check if any products are selected or any variants are selected
    const hasSelectedProducts = selectedProductsForDialog.length > 0;
    const hasSelectedVariants = Object.values(selectedVariantsForDialog).some(variants => 
      Object.values(variants).some(selected => selected)
    );
    
    if (!hasSelectedProducts && !hasSelectedVariants) {
      return;
    }

    // For this simple case, just take the first selected product/variant
    if (hasSelectedVariants) {
      // Find first selected variant
      for (const [productId, variants] of Object.entries(selectedVariantsForDialog)) {
        for (const [variantId, isSelected] of Object.entries(variants)) {
          if (isSelected) {
            setFormData(prev => ({
              ...prev,
              product_id: productId,
              variant_id: variantId
            }));
            break;
          }
        }
        break;
      }
    } else if (hasSelectedProducts) {
      // Take first selected product
      setFormData(prev => ({
        ...prev,
        product_id: selectedProductsForDialog[0],
        variant_id: ''
      }));
    }
    
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setSelectedProductsForDialog([]);
    setSelectedVariantsForDialog({});
  };

  // Filter function for product selection dialog
  const getFilteredProductsForSelection = () => {
    if (!productSearchTerm) return products || [];
    
    const searchLower = productSearchTerm.toLowerCase();
    return (products || []).filter(product => 
      product.name.toLowerCase().includes(searchLower) ||
      (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
      (product.brand && product.brand.name && product.brand.name.toLowerCase().includes(searchLower))
    );
  };

  const handleSubmit = async () => {
    try {
      const capacityData = {
        product_id: formData.product_id,
        variant_id: formData.variant_id || null,
        capacity: formData.capacity,
        setup_time: parseInt(formData.setupTime) || 0,
        cleanup_time: parseInt(formData.cleanupTime) || 0
      };

      if (isNewLocation) {
        // For new locations, add to form state (don't save to database yet)
        if (editMode) {
          // Update existing item in form state
          const updatedCapacities = formCapacities.map(cap => 
            cap.id === formData.id ? { ...capacityData, id: formData.id } : cap
          );
          setFormCapacities(updatedCapacities);
        } else {
          // Add new item to form state
          const newCapacity = { ...capacityData, id: `temp-${Date.now()}` };
          setFormCapacities([...formCapacities, newCapacity]);
        }
        handleCloseDialog();
        return; // Don't continue to database save
      } else {
        // For existing locations, save directly to database
        const dbCapacityData = {
          ...capacityData,
          company_id: companyId,
          location_id: locationId
        };

        if (editMode) {
          const { error } = await supabase
            .from('location_product_capacities')
            .update(dbCapacityData)
            .eq('id', formData.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('location_product_capacities')
            .insert([dbCapacityData]);
          if (error) throw error;
        }
        
        await loadCapacities();
      }

      handleCloseDialog();
    } catch (err) {
      console.error('Error saving capacity:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (isNewLocation) {
        // For new locations, remove from form state
        const updatedCapacities = formCapacities.filter(cap => cap.id !== id);
        setFormCapacities(updatedCapacities);
      } else {
        // For existing locations, delete from database
        const { error } = await supabase
          .from('location_product_capacities')
          .delete()
          .eq('id', id);

        if (error) throw error;
        await loadCapacities();
      }
    } catch (err) {
      console.error('Error deleting capacity:', err);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: 400 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
          Product Capacities
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 3,
            fontWeight: 500,
            px: 3,
            py: 1
          }}
        >
          Add Product
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500, color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}` }}>
                Product
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}` }}>
                Capacity
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}` }}>
                Setup Time (minutes)
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}` }}>
                Cleanup Time (minutes)
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 500, color: 'primary.main', borderBottom: `2px solid ${theme.palette.primary.main}`, width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(isNewLocation ? formCapacities : capacities).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No product capacities defined. Click "Add Product" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (isNewLocation ? formCapacities : capacities).map((capacity) => (
                <TableRow key={capacity.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {(() => {
                      if (isNewLocation) {
                        // For new locations, find product from products array
                        const product = products.find(p => p.id === capacity.product_id);
                        if (!product) return 'Unknown Product';
                        
                        let displayText = product.name;
                        
                        if (capacity.variant_id && product.variants) {
                          const variant = product.variants.find(v => v.id === capacity.variant_id);
                          if (variant) {
                            displayText += ` - ${variant.name || `${variant.volume_weight}${variant.volume_unit}`}`;
                          }
                        }
                        
                        return `${displayText} (${product.sku || 'No SKU'})`;
                      } else {
                        // Use the product data from the capacity record (includes variants)
                        const product = capacity.product;
                        if (!product) return 'Unknown Product';
                        
                        let displayText = product.name;
                        
                        // If there's a variant, show variant info
                        if (capacity.variant_id && product.variants) {
                          const variant = product.variants.find(v => v.id === capacity.variant_id);
                          if (variant) {
                            displayText += ` - ${variant.name || `${variant.volume_weight}${variant.volume_unit}`}`;
                          }
                        }
                        
                        return `${displayText} (${product.sku || 'No SKU'})`;
                      }
                    })()}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{capacity.capacity}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{capacity.setup_time}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{capacity.cleanup_time}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(capacity)}
                      sx={{
                        mr: 1,
                        color: 'text.primary',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(capacity.id)}
                      sx={{
                        color: 'error.main',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'error.main',
                          color: 'white'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for adding/editing product capacities */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500, fontSize: '1.25rem' }}>
          {editMode ? 'Edit Product Capacity' : 'Add Product Capacity'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gap: 2.5, mt: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Product
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenProductSelectionDialog}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 2,
                  px: 2,
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  color: formData.product_id ? 'text.primary' : 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                {formData.product_id ? 
                  (() => {
                    const product = products.find(p => p.id === formData.product_id);
                    return product ? `${product.name} (${product.sku || 'No SKU'})` : 'Select Product';
                  })() :
                  'Select Product'
                }
              </Button>
            </Box>
            
            {/* Variant Selection */}
            {formData.product_id && (() => {
              const selectedProduct = products.find(p => p.id === formData.product_id);
              return selectedProduct && selectedProduct.variants && selectedProduct.variants.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Product Variant</InputLabel>
                  <Select
                    value={formData.variant_id || ''}
                    label="Product Variant"
                    onChange={(e) => setFormData({ ...formData, variant_id: e.target.value })}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      Base Product (No Variant)
                    </MenuItem>
                    {selectedProduct.variants.map((variant) => (
                      <MenuItem key={variant.id} value={variant.id} sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {variant.name || `${variant.volume_weight}${variant.volume_unit}`}
                        {variant.sku && ` (${variant.sku})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null;
            })()}

            <TextField
              label="Capacity"
              fullWidth
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              placeholder="e.g. 100 pallets, 500 units, 2000 bottles"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '& .MuiInputBase-input': { fontSize: '0.9rem', fontWeight: 500 }
                }
              }}
            />
            <TextField
              label="Setup Time (minutes)"
              fullWidth
              type="number"
              value={formData.setupTime}
              onChange={(e) => setFormData({ ...formData, setupTime: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '& .MuiInputBase-input': { fontSize: '0.9rem', fontWeight: 500 }
                }
              }}
            />
            <TextField
              label="Cleanup Time (minutes)"
              fullWidth
              type="number"
              value={formData.cleanupTime}
              onChange={(e) => setFormData({ ...formData, cleanupTime: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '& .MuiInputBase-input': { fontSize: '0.9rem', fontWeight: 500 }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ borderRadius: 3, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ borderRadius: 3, fontWeight: 500, px: 3 }}
          >
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={productSelectionDialogOpen}
        onClose={handleCloseProductSelectionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Product
          </Typography>
          <IconButton onClick={handleCloseProductSelectionDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
        }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search products by name, barcode, or brand..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                  fontWeight: 500
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Box>
          
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[100], 0.8) }}>
                  <TableCell sx={{ width: 60, pl: 3, fontWeight: 500 }}></TableCell>
                  <TableCell sx={{ fontWeight: 500, minWidth: 200, pl: 2, pr: 3 }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 500, minWidth: 140, px: 2 }}>Brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredProductsForSelection().map(product => (
                  <React.Fragment key={product.id}>
                    <TableRow 
                      hover
                      selected={selectedProductsForDialog.includes(product.id)}
                      onClick={() => {
                        if (selectedProductsForDialog.includes(product.id)) {
                          setSelectedProductsForDialog(selectedProductsForDialog.filter(id => id !== product.id));
                        } else {
                          setSelectedProductsForDialog([...selectedProductsForDialog, product.id]);
                        }
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.success.main, 0.12)
                          }
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.grey[100], 0.8)
                        },
                        borderLeft: selectedProductsForDialog.includes(product.id) ? `3px solid ${theme.palette.success.main}` : '3px solid transparent'
                      }}
                    >
                      <TableCell sx={{ width: 60, pl: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: selectedProductsForDialog.includes(product.id) ? 'success.main' : 'grey.400',
                          bgcolor: selectedProductsForDialog.includes(product.id) ? 'success.main' : 'transparent',
                          color: selectedProductsForDialog.includes(product.id) ? 'white' : 'transparent',
                          transition: 'all 0.2s ease',
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          ✓
                        </Box>
                      </TableCell>
                      <TableCell sx={{ pl: 2, pr: 3 }}>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            {product.name}
                          </Typography>
                          {product.variants && product.variants.length > 0 ? (
                            <Chip
                              size="small"
                              label={`${product.variants.length} variants`}
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {product.brand?.name || 'No Brand'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Display variant selection */}
                    {product.variants && product.variants.length > 0 && selectedProductsForDialog.includes(product.id) && (
                      <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.02) }}>
                        <TableCell colSpan={3} sx={{ py: 2, px: 4 }}>
                          <Box sx={{ pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                Select Variants ({getSelectedVariantsCount(product.id, product.variants)} of {product.variants.length} selected):
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSelectAllVariants(product.id, product.variants)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  color: 'success.main',
                                  borderColor: 'success.main',
                                  '&:hover': {
                                    borderColor: 'success.dark',
                                    bgcolor: alpha(theme.palette.success.main, 0.08)
                                  }
                                }}
                              >
                                {areAllVariantsSelected(product.id, product.variants) ? 'Unselect All' : 'Select All'}
                              </Button>
                            </Box>
                            <Grid container spacing={2}>
                              {product.variants.map((variant, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={variant.id || idx}>
                                  <Paper
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: selectedVariantsForDialog[product.id]?.[variant.id]
                                        ? `2px solid ${theme.palette.success.main}`
                                        : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      bgcolor: selectedVariantsForDialog[product.id]?.[variant.id] 
                                        ? alpha(theme.palette.success.main, 0.12)
                                        : alpha(theme.palette.grey[50], 0.8),
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        bgcolor: selectedVariantsForDialog[product.id]?.[variant.id] 
                                          ? alpha(theme.palette.success.main, 0.18)
                                          : alpha(theme.palette.grey[100], 0.8),
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.shadows[4]
                                      }
                                    }}
                                    onClick={() => handleVariantSelection(product.id, variant.id, !selectedVariantsForDialog[product.id]?.[variant.id])}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                      <Checkbox
                                        checked={!!selectedVariantsForDialog[product.id]?.[variant.id]}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleVariantSelection(product.id, variant.id, e.target.checked);
                                        }}
                                        size="small"
                                        color="success"
                                        sx={{ 
                                          p: 0.5,
                                          '&.Mui-checked': {
                                            color: 'success.main'
                                          }
                                        }}
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                          {variant.name || 
                                           (variant.volume_weight ? `${variant.volume_weight}${variant.volume_unit || ''}` : 'Unnamed variant')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                          {variant.sku ? `SKU: ${variant.sku}` : 'No SKU'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {getFilteredProductsForSelection().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          No products found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {productSearchTerm ? 'Try adjusting your search criteria' : 'No products available'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => window.open(`/company/${companyId}/products`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Product
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseProductSelectionDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmProductSelection}
              variant="contained"
              disabled={(() => {
                const hasSelectedProducts = selectedProductsForDialog.length > 0;
                const hasSelectedVariants = Object.values(selectedVariantsForDialog).some(variants => 
                  Object.values(variants).some(selected => selected)
                );
                return !hasSelectedProducts && !hasSelectedVariants;
              })()}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              Confirm Selection
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductCapacitiesTab;